/**
 * Client Component wrapper for EventsThemeBlock
 * Provides EventDrawerProvider context for drawer functionality
 * Uses @uic/events package components
 */

'use client';

import { BlockLayout } from '@/components/blocks/BlockLayout';
import type { EventTeaser, EventType } from '@uic/events';
import { EventCard, EventDrawerProvider } from '@uic/events';

interface EventsThemeBlockClientProps {
  /** Events data from server */
  events: EventTeaser[];
  /** Optional title for the block */
  title?: string;
  /** Layout style */
  layout?: 'default' | 'grid' | 'list' | 'compact' | 'featured';
  /** Base URL for event links */
  baseUrl?: string;
  /** Custom CSS classes */
  className?: string;
  /** Empty state message */
  emptyMessage?: string;
  /** URL to redirect to when the "See All" button is clicked */
  seeAllUrl?: string;
}

/**
 * Grid container classes based on layout
 * Maps layout variants to Tailwind grid classes
 */
const gridClassesMap: Record<string, string> = {
  grid: 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  list: 'flex flex-col gap-4',
  compact: 'flex flex-col gap-1',
  default: 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3',
  featured: 'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3',
};

export function EventsThemeBlockClient({
  events,
  title,
  layout = 'default',
  baseUrl = '/events',
  className = '',
  emptyMessage = 'No events found for the selected theme(s).',
  seeAllUrl,
}: EventsThemeBlockClientProps) {
  const gridClasses = gridClassesMap[layout] || gridClassesMap.default;

  return (
    <EventDrawerProvider>
      <BlockLayout<EventTeaser>
        title={title}
        seeAllUrl={seeAllUrl}
        data={events}
        renderItem={event => (
          <EventCard
            event={event as unknown as EventType}
            baseUrl={baseUrl}
            variant={layout}
            showPastIndicator={true}
            clickable={false} // Non-clickable with Details button, opens drawer
          />
        )}
        className={className}
        showEmptyState={false}
        emptyMessage={emptyMessage}
        gridClasses={gridClasses}
        orientation="horizontal"
        itemWidth={320}
      />
    </EventDrawerProvider>
  );
}

